import React, { useCallback, useEffect, useState } from 'react';
import Axios from '../../api/axios';
import Dict from '../../messages.js';

function formatWhen(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString('tr-TR');
    } catch {
        return String(value);
    }
}

/**
 * Cari kartı içinde SSMedia WhatsApp yazışmaları (metin + görsel).
 */
export default function WhatsAppConversationsPanel({ carRef, phone, leadId, visible }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);

    const load = useCallback(async () => {
        if (!carRef && !phone && !leadId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/whatsappConversations`,
                {
                    params: {
                        CAR_REF: carRef || undefined,
                        phone: phone || undefined,
                        leadId: leadId || undefined,
                        limit: 300,
                    },
                }
            );
            setPayload(res.data || null);
            const sync = res.data?.resimSync;
            if (sync?.attached > 0) {
                // Resim Gir galerisine yeni WA görselleri eklendi
                console.info(
                    `[WhatsApp] ${sync.attached} görsel cari resime bağlandı` +
                        (sync.skipped ? ` (${sync.skipped} zaten vardı)` : '')
                );
            }
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'WhatsApp yazışmaları alınamadı');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [carRef, phone, leadId]);

    useEffect(() => {
        if (!visible) return;
        load();
    }, [visible, load]);

    if (!visible) return null;

    const conversations = payload?.conversations || [];

    return (
        <div className="cari-whatsapp-panel" style={{ padding: 8, minHeight: 240 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>{Dict.get('WhatsApp Yazışmaları') || 'WhatsApp Yazışmaları'}</strong>
                <button type="button" className="dx-button dx-button-mode-outlined" onClick={load}>
                    Yenile
                </button>
            </div>
            {loading ? <div style={{ color: '#666' }}>Yükleniyor...</div> : null}
            {error ? <div style={{ color: '#b00020', marginBottom: 8 }}>{error}</div> : null}
            {payload && payload.enabled === false ? (
                <div style={{ color: '#666' }}>WhatsApp sekmesi tenant ayarında kapalı.</div>
            ) : null}
            {payload?.resimSync?.ok && (payload.resimSync.attached > 0 || payload.resimSync.skipped > 0) ? (
                <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 8 }}>
                    Cari resim: {payload.resimSync.attached || 0} yeni görsel bağlandı
                    {payload.resimSync.skipped
                        ? `, ${payload.resimSync.skipped} zaten kayıtlı`
                        : ''}
                    . Resim Gir sekmesinden görebilirsiniz.
                </div>
            ) : null}
            {payload?.resimSync && payload.resimSync.ok === false ? (
                <div style={{ fontSize: 12, color: '#b26a00', marginBottom: 8 }}>
                    Görseller cari resime bağlanamadı: {payload.resimSync.message || 'SSMEDIA_API_URL / WORKER_SYNC_SECRET kontrol edin'}
                </div>
            ) : null}
            {!loading && !error && payload?.enabled !== false && !conversations.length ? (
                <div style={{ color: '#666' }}>Bu cari için WhatsApp yazışması bulunamadı.</div>
            ) : null}
            {conversations.map((conv) => (
                <div
                    key={conv.id}
                    style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        marginBottom: 12,
                        padding: 10,
                        background: '#fafafa',
                    }}
                >
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                        {conv.contactName || conv.phone || conv.id}
                        {conv.handoffState ? ` · ${conv.handoffState}` : ''}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
                        {(conv.messages || []).map((msg) => {
                            const inbound = String(msg.direction || '').toUpperCase() === 'INBOUND';
                            return (
                                <div
                                    key={msg.id}
                                    style={{
                                        alignSelf: inbound ? 'flex-start' : 'flex-end',
                                        maxWidth: '85%',
                                        background: inbound ? '#fff' : '#dcf8c6',
                                        border: '1px solid #ddd',
                                        borderRadius: 10,
                                        padding: '8px 10px',
                                    }}
                                >
                                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                                        {msg.body || (msg.attachments?.length ? '' : '—')}
                                    </div>
                                    {(msg.attachments || []).length > 0 ? (
                                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {msg.attachments.map((att) => {
                                                const url = `${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/whatsappAttachment?messageId=${encodeURIComponent(msg.id)}&index=${att.index}`;
                                                const isImage = String(att.kind || '').toLowerCase() === 'image'
                                                    || String(att.mimeType || '').startsWith('image/');
                                                if (isImage) {
                                                    return (
                                                        <a key={`${msg.id}-${att.index}`} href={url} target="_blank" rel="noreferrer">
                                                            <img
                                                                src={url}
                                                                alt={att.filename || att.caption || 'görsel'}
                                                                style={{
                                                                    maxWidth: 180,
                                                                    maxHeight: 180,
                                                                    borderRadius: 6,
                                                                    display: 'block',
                                                                    objectFit: 'cover',
                                                                }}
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = 'none';
                                                                }}
                                                            />
                                                        </a>
                                                    );
                                                }
                                                return (
                                                    <a
                                                        key={`${msg.id}-${att.index}`}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ fontSize: 12 }}
                                                    >
                                                        {att.filename || att.kind || 'dosya'}
                                                        {att.caption ? ` — ${att.caption}` : ''}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                    <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>
                                        {formatWhen(msg.createdAt)}
                                        {msg.status ? ` · ${msg.status}` : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
